// Pure helpers for snapshot/selection mutations driven by non-undoable inserts.

import { ReviewEditorAction, ReviewEditorSnapshot, SelectionRef } from './undo-actions';

export interface UndoRedoPluginLike {
  doneActions: unknown[];
  undoneActions: unknown[];
}

export function walkActivePageActions(
  plugin: UndoRedoPluginLike | undefined | null,
  activePage: number,
  visit: (action: ReviewEditorAction) => void,
): void {
  if (!plugin || !Array.isArray(plugin.doneActions) || !Array.isArray(plugin.undoneActions)) {
    return;
  }
  const process = (action: unknown): void => {
    if (action instanceof ReviewEditorAction && action.snapshot?.activePage === activePage) {
      visit(action);
    }
  };
  plugin.doneActions.forEach(process);
  plugin.undoneActions.forEach(process);
}

// Use snapshot's own column count so the inserted row matches the snapshot's width
// even when column merges have changed the live column count.
export function patchSnapshotForRowInsert(
  snapshot: ReviewEditorSnapshot,
  rowIndex: number,
  numMetadataRows: number,
  buildEmptyCellRow: (numCol: number) => any[],
): void {
  const pageDataNumCol = snapshot.pageData?.[0]?.length ?? snapshot.cells?.[0]?.length ?? 0;
  if (snapshot.pageData) {
    snapshot.pageData.splice(rowIndex, 0, Array(pageDataNumCol).fill(''));
  }
  const cellsIndex = rowIndex - numMetadataRows;
  if (cellsIndex >= 0 && snapshot.cells) {
    const cellsNumCol = snapshot.cells[0]?.length ?? pageDataNumCol;
    snapshot.cells.splice(cellsIndex, 0, buildEmptyCellRow(cellsNumCol));
  }
  shiftSelectionBeforeForRowInsert(snapshot.selectionBefore, rowIndex);
}

export function patchSnapshotForColumnInsert(
  snapshot: ReviewEditorSnapshot,
  colIndex: number,
  buildEmptyCell: () => any,
  buildEmptyStatementOption: () => any,
): void {
  snapshot.pageData?.forEach((row: any[]) => row?.splice(colIndex, 0, ''));
  snapshot.cells?.forEach((row: any[]) => row?.splice(colIndex, 0, buildEmptyCell()));
  // Keep metadata col count in sync so _setPageTabularData rebuilds a uniform-width grid.
  snapshot.metadataPage?.forEach((row: any[]) => row?.splice(colIndex, 0, ''));
  // Mirror live insertColumn which pushes to end (not splices at colIndex).
  if (Array.isArray(snapshot.statementOptionsPage) && snapshot.statementOptionsPage.length !== 0) {
    snapshot.statementOptionsPage.push(buildEmptyStatementOption());
  }
  shiftSelectionBeforeForColumnInsert(snapshot.selectionBefore, colIndex);
}

// Shift row entries in Handsontable's [row1,col1,row2,col2] coords so undo re-highlights the right rows.
function shiftSelectionBeforeForRowInsert(
  selectionBefore: number[][] | null | undefined,
  insertedAt: number,
): void {
  if (!Array.isArray(selectionBefore)) return;
  for (const entry of selectionBefore) {
    if (entry.length >= 4) {
      if (entry[0] >= insertedAt) entry[0] += 1;
      if (entry[2] >= insertedAt) entry[2] += 1;
    }
  }
}

function shiftSelectionBeforeForColumnInsert(
  selectionBefore: number[][] | null | undefined,
  insertedAt: number,
): void {
  if (!Array.isArray(selectionBefore)) return;
  for (const entry of selectionBefore) {
    if (entry.length >= 4) {
      if (entry[1] >= insertedAt) entry[1] += 1;
      if (entry[3] >= insertedAt) entry[3] += 1;
    }
  }
}

export function shiftSelectionForRowInsert(
  selectionRef: SelectionRef | undefined,
  insertedAt: number,
): void {
  shiftSelectionForRowInsertBy(selectionRef, insertedAt, 1);
}

// Bulk variant used by insertFrom, which inserts multiple rows at once.
export function shiftSelectionForRowInsertBy(
  selectionRef: SelectionRef | undefined,
  insertedAt: number,
  numRows: number,
): void {
  if (!selectionRef || numRows <= 0) return;
  for (const range of selectionRef.selection) {
    if (range.start.row >= insertedAt) range.start.row += numRows;
    if (range.end.row >= insertedAt) range.end.row += numRows;
  }
}

export function shiftSelectionForColumnInsert(
  selectionRef: SelectionRef | undefined,
  insertedAt: number,
): void {
  if (!selectionRef) return;
  for (const range of selectionRef.selection) {
    if (range.start.col >= insertedAt) range.start.col += 1;
    if (range.end.col >= insertedAt) range.end.col += 1;
  }
}

// insertFrom copies content from another page. To preserve that content across a
// subsequent undo, the caller must capture the inserted rows BEFORE the source
// page is deleted, then hand them to this helper. Widening is always at the end
// so column selection coords never shift; only rows do.
export function patchSnapshotForInsertFrom(
  snapshot: ReviewEditorSnapshot,
  rowIndex: number,
  columnsWidenedBy: number,
  numMetadataRows: number,
  insertedCellRows: any[][],
  insertedPageDataRows: any[][],
  buildEmptyCell: () => any,
  buildEmptyStatementOption: () => any,
): void {
  for (let i = 0; i < columnsWidenedBy; i++) {
    snapshot.pageData?.forEach((row: any[]) => row?.push(''));
    snapshot.cells?.forEach((row: any[]) => row?.push(buildEmptyCell()));
    snapshot.metadataPage?.forEach((row: any[]) => row?.push(''));
    if (Array.isArray(snapshot.statementOptionsPage) && snapshot.statementOptionsPage.length !== 0) {
      snapshot.statementOptionsPage.push(buildEmptyStatementOption());
    }
  }
  const numRowsInserted = insertedPageDataRows.length;
  const cellsIndex = rowIndex - numMetadataRows;
  for (let i = 0; i < numRowsInserted; i++) {
    if (snapshot.pageData) {
      snapshot.pageData.splice(rowIndex + i, 0, JSON.parse(JSON.stringify(insertedPageDataRows[i])));
    }
    if (cellsIndex >= 0 && snapshot.cells && insertedCellRows[i]) {
      snapshot.cells.splice(cellsIndex + i, 0, JSON.parse(JSON.stringify(insertedCellRows[i])));
    }
  }
  if (Array.isArray(snapshot.selectionBefore) && numRowsInserted > 0) {
    for (const entry of snapshot.selectionBefore) {
      if (entry.length >= 4) {
        if (entry[0] >= rowIndex) entry[0] += numRowsInserted;
        if (entry[2] >= rowIndex) entry[2] += numRowsInserted;
      }
    }
  }
}
